// crewbit-admin/src/components/money/RemittanceLogTable.tsx
//
// Sortable <table> for the admin Remittance Log. Reads from useRemittanceLog.
// Empty state per calm-empty discipline: table chrome + 1 empty-state row,
// NOT a separate empty-state component (Hard Rule #4).
//
// Default sort: period_end DESC. Sortable columns: Period / Tax Type /
// Status / Amount Owed / Filed Date.
//
// Spec §Tax.7.2 + §3d. Sort precedent matches Session 123 OutstandingTable.

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { RemittanceLogRow } from '../../hooks/useRemittanceLog';
import { palette, admin, spacing, shadow } from '../../styles/tokens';
import { RemittanceLogTableRow } from './RemittanceLogTableRow';

type SortKey = 'period_end' | 'tax_type' | 'status' | 'amount_owed' | 'filed_at';
type SortDir = 'asc' | 'desc';

interface RemittanceLogTableProps {
  rows: RemittanceLogRow[] | null;
  isLoading: boolean;
  onRowClick: (row: RemittanceLogRow) => void;
}

const STATUS_ORDER: Record<RemittanceLogRow['status'], number> = {
  open: 0,
  reopened: 1,
  amended: 2,
  filed: 3,
};

function compareRows(a: RemittanceLogRow, b: RemittanceLogRow, key: SortKey, dir: SortDir): number {
  const sign = dir === 'asc' ? 1 : -1;
  switch (key) {
    case 'period_end': {
      const at = new Date(a.period_end).getTime();
      const bt = new Date(b.period_end).getTime();
      return (at - bt) * sign;
    }
    case 'tax_type':
      return a.tax_type.localeCompare(b.tax_type) * sign;
    case 'status':
      return (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) * sign;
    case 'amount_owed':
      return (a.amount_owed - b.amount_owed) * sign;
    case 'filed_at': {
      const at = a.filed_at ? new Date(a.filed_at).getTime() : 0;
      const bt = b.filed_at ? new Date(b.filed_at).getTime() : 0;
      return (at - bt) * sign;
    }
  }
}

const thBaseStyle: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 11.5,
  fontWeight: 600,
  color: palette.g500,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`,
  background: palette.g50,
  cursor: 'pointer',
  userSelect: 'none',
};

interface SortableThProps {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  align?: 'left' | 'right';
  onClick: (key: SortKey) => void;
}

function SortableTh({ label, sortKey, currentKey, currentDir, align = 'left', onClick }: SortableThProps) {
  const isActive = currentKey === sortKey;
  return (
    <th
      onClick={() => onClick(sortKey)}
      style={{ ...thBaseStyle, textAlign: align }}
      aria-sort={isActive ? (currentDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: isActive ? palette.g700 : palette.g500 }}>
        {label}
        {isActive && (currentDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
      </span>
    </th>
  );
}

export function RemittanceLogTable({ rows, isLoading, onRowClick }: RemittanceLogTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('period_end');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sortedRows = useMemo(() => {
    if (!rows) return null;
    return [...rows].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [rows, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'tax_type' ? 'asc' : 'desc');
    }
  };

  return (
    <div
      style={{
        background: palette.white,
        border: `1px solid ${palette.g150}`,
        borderRadius: admin.cardRadius,
        boxShadow: shadow.xs,
        overflow: 'hidden',
        marginTop: spacing.lg,
      }}
    >
      <div
        style={{
          padding: `${spacing.md}px ${spacing.lg}px`,
          borderBottom: `1px solid ${palette.g100}`,
          fontSize: 14,
          fontWeight: 600,
          color: admin.header,
        }}
      >
        Remittance log
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <SortableTh label="Period" sortKey="period_end" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} />
            <SortableTh label="Tax type" sortKey="tax_type" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} />
            <SortableTh label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} />
            <SortableTh label="Amount owed" sortKey="amount_owed" currentKey={sortKey} currentDir={sortDir} align="right" onClick={handleSort} />
            <SortableTh label="Filed" sortKey="filed_at" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} />
          </tr>
        </thead>
        <tbody>
          {sortedRows && sortedRows.length > 0 ? (
            sortedRows.map((row) => (
              <RemittanceLogTableRow key={row.id} row={row} onClick={onRowClick} />
            ))
          ) : (
            <tr>
              <td
                colSpan={5}
                style={{
                  padding: `${spacing.lg}px ${spacing.lg}px`,
                  textAlign: 'center',
                  fontSize: 13,
                  color: palette.g500,
                }}
              >
                {isLoading ? 'Loading…' : 'No filings yet — your first remittance will appear here.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
